import type { HeDescriptorDetails, Programme } from "@/types";

import { isFeAwardType, isHeAwardType } from "./award-types";

export interface NarrativeFieldDefinition {
  field: keyof HeDescriptorDetails;
  label: string;
}

export interface NarrativeTemplateDefinition {
  id: "qqi-he" | "qqi-fet-cas";
  title: string;
  description: string;
  fields: readonly NarrativeFieldDefinition[];
  includesHeMetadata: boolean;
  includesPsrb: boolean;
  includesAppendix: boolean;
}

const SHARED_FIELDS: readonly NarrativeFieldDefinition[] = [
  { field: "programmeSynopsis", label: "Programme synopsis" },
  { field: "rationale", label: "Programme rationale" },
  { field: "targetGroup", label: "Education and training need / target group" },
  {
    field: "teachingLearningAssessmentStrategy",
    label: "Teaching, learning and assessment strategy",
  },
  { field: "accessRequirements", label: "Access and entry requirements" },
  { field: "rplArrangements", label: "Recognition of prior learning arrangements" },
  { field: "assessmentIntegrityNarrative", label: "Integrity of assessment" },
  { field: "learningEnvironmentNarrative", label: "Programme learning environment" },
  { field: "staffingPlan", label: "Staffing and recruitment plan" },
  { field: "resourceRequirements", label: "Physical and digital resources" },
];

export const NARRATIVE_TEMPLATES: readonly NarrativeTemplateDefinition[] = [
  {
    id: "qqi-he",
    title: "QQI higher education programme narrative",
    description:
      "Capture the authored programme context, learner journey, delivery strategy, resources and supporting validation evidence.",
    fields: [{ field: "graduateAttributes", label: "Graduate attributes" }, ...SHARED_FIELDS],
    includesHeMetadata: true,
    includesPsrb: true,
    includesAppendix: true,
  },
  {
    id: "qqi-fet-cas",
    title: "QQI FET/CAS programme narrative",
    description:
      "Capture the provider-authored context and delivery arrangements that complement the selected CAS award and component requirements.",
    fields: SHARED_FIELDS,
    includesHeMetadata: false,
    includesPsrb: false,
    includesAppendix: false,
  },
];

function usesFetNarrative(programme: Programme): boolean {
  const awardType = (programme.awardType ?? "").trim();
  // Before the CAS evidence: a code or a CAS module survives a switch to HE.
  if (isHeAwardType(awardType)) {
    return false;
  }
  return (
    isFeAwardType(awardType) ||
    awardType.startsWith("FET ") ||
    awardType === "Advanced Certificate" ||
    Boolean(programme.casMajorAwardCode) ||
    (programme.modules ?? []).some((module) => module.casSource)
  );
}

export function resolveNarrativeTemplate(programme: Programme): NarrativeTemplateDefinition {
  const id = usesFetNarrative(programme) ? "qqi-fet-cas" : "qqi-he";
  return NARRATIVE_TEMPLATES.find((template) => template.id === id)!;
}
